/* eslint-disable complexity */
import { skipToken } from '@reduxjs/toolkit/query';
import {
    CONTRACT_RATE,
    FMH_NPI,
    RouteCodeOptions,
    RouteCodes,
    UpdateSupplierReasons,
} from '@strata/constants';
import { Checkbox } from '@strata/react-checkbox-list';
import { ReasonInput } from '@strata/react-compound-inputs';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { Spinner } from '@strata/react-loading-animation';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { ServicingRate } from '@strata/react-servicing-rate';
import { validationStrings } from '@strata/strata-ui-strings';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { transferTreatment } from 'api/treatmentAPI';
import { ActionRateOverride } from 'components/referrals/dialogs/ActionRateOverride';
import { UNSPECIFIED_NPI } from 'services/constants';
import { usePostDetermineReferralRatesQuery } from 'store/api/rateSetterApi';
import { useGetAllSuppliersQuery, useGetEligibleSuppliersQuery } from 'store/api/referenceApi';

const isFmhSupplier = (supplierNpi) => supplierNpi === FMH_NPI || supplierNpi === UNSPECIFIED_NPI;

const MethodOptions = RouteCodeOptions.filter(({ value }) =>
    [RouteCodes.ACCEPT, RouteCodes.CONTRACT, RouteCodes.DIRECT, RouteCodes.ROFR].includes(value),
);

export const UpdateActiveReferralDataSupplier = ({
    treatmentDetails = undefined,
    memberDetails = undefined,
}) => {
    const formik = useFormikContext();

    const {
        activeReferral,
        payerId,
        productServiceOid,
        segmentId,
        benefitType,
        isBrand,
        placeOfService,
    } = treatmentDetails ?? {};
    const { servicingSupplierNpi } = activeReferral ?? {};

    const { data: eligibleSuppliers, isLoading: eligibleSuppliersIsLoading } =
        useGetEligibleSuppliersQuery({
            payerId,
            segmentId,
            memberState: memberDetails.memberState,
            productServiceOid,
            benefit: benefitType,
            placeOfService,
            isBrand,
            lineOfBusiness: memberDetails.lineOfBusiness,
            lobSegment: memberDetails.lobSegment,
        });
    const { data: allSuppliersData, isFetching: allSuppliersFetching } = useGetAllSuppliersQuery();
    const { supplierList } = allSuppliersData ?? {};
    // Add the internal NPIs to the supplier list
    const supplierOptions = [
        { name: 'Free Market Health', secondaryData: FMH_NPI, npi: FMH_NPI },
        { name: 'Unspecified', secondaryData: UNSPECIFIED_NPI, npi: UNSPECIFIED_NPI },
        ...(eligibleSuppliers ?? []),
    ].filter(({ npi }) => npi !== servicingSupplierNpi);
    const { data: rateRule, isLoading: rateRuleIsLoading } = usePostDetermineReferralRatesQuery(
        treatmentDetails && memberDetails
            ? {
                  payerId,
                  segmentId,
                  productServiceOid,
                  benefit: benefitType,
                  isBrand,
                  memberId: memberDetails.memberId,
                  memberFirstName: memberDetails.firstName,
                  memberDateOfBirth: memberDetails.dateOfBirth,
                  routeCode: RouteCodes.ROFR,
                  placeOfService,
                  lineOfBusiness: memberDetails.lineOfBusiness,
              }
            : skipToken,
    );

    return eligibleSuppliersIsLoading || allSuppliersFetching || rateRuleIsLoading ? (
        <Spinner className="padding__around--large" size={Spinner.Size.LARGE} />
    ) : (
        <>
            <SuggestSingleSelectFormik
                id="supplierNpi"
                label="Pharmacy"
                disabled={formik.values.outOfNetworkPharmacy === true}
                required
                values={supplierOptions}
                selectedItem={supplierOptions.find(({ npi }) => npi === formik.values.supplierNpi)}
                onChange={(newValue) =>
                    formik.setValues({
                        ...formik.values,
                        supplierNpi: newValue?.npi,
                        ...(isFmhSupplier(newValue?.npi)
                            ? {
                                  method: RouteCodes.ROFR,
                                  ...ActionRateOverride.initialValues,
                                  useContractRate: undefined,
                              }
                            : undefined),
                    })
                }
            />
            <Checkbox
                id="outOfNetworkPharmacy"
                label="Out of Network Pharmacy"
                onChange={async (newValue) => {
                    if (newValue === true) {
                        await formik.setFieldValue('supplierNpi', undefined);
                    } else {
                        await formik.setFieldValue('outOfNetworkSupplierNpi', undefined);
                    }
                    await formik.setFieldValue('outOfNetworkPharmacy', newValue);
                }}
                checked={formik.values.outOfNetworkPharmacy ?? false}
            />
            {formik.values.outOfNetworkPharmacy === true && (
                <SuggestSingleSelectFormik
                    id="outOfNetworkSupplierNpi"
                    label="Out of Network Pharmacy"
                    required
                    values={supplierList}
                    selectedItem={supplierList?.find(
                        ({ npi }) => npi === formik.values.outOfNetworkSupplierNpi,
                    )}
                    onChange={(newValue) =>
                        formik.setValues({
                            ...formik.values,
                            outOfNetworkSupplierNpi: newValue?.npi,
                            ...(isFmhSupplier(newValue?.npi)
                                ? {
                                      method: RouteCodes.ROFR,
                                      ...ActionRateOverride.initialValues,
                                      useContractRate: undefined,
                                  }
                                : undefined),
                        })
                    }
                />
            )}
            {(formik.values.supplierNpi || formik.values.outOfNetworkSupplierNpi) && (
                <>
                    {!isFmhSupplier(
                        formik.values.supplierNpi || formik.values.outOfNetworkSupplierNpi,
                    ) && (
                        <>
                            <SuggestSingleSelectFormik
                                id="method"
                                label="Method"
                                required
                                values={MethodOptions}
                                selectedItem={MethodOptions.find(
                                    ({ value }) => value === formik.values.method,
                                )}
                                onChange={({ value }) =>
                                    formik.setValues({
                                        ...formik.values,
                                        method: value,
                                        ...ActionRateOverride.initialValues,
                                        useContractRate: undefined,
                                    })
                                }
                            />
                            {formik.values.method !== RouteCodes.CONTRACT && (
                                <>
                                    <div
                                        className="margin__bottom--medium"
                                        style={{ display: 'flex' }}
                                    >
                                        <DetailLabelValuePair
                                            className="margin__right--x-large"
                                            label="Default Rate"
                                            value={
                                                <ServicingRate
                                                    servicingRate={rateRule?.defaultRate}
                                                    referentPriceType={rateRule?.referentPriceType}
                                                />
                                            }
                                        />
                                        <DetailLabelValuePair
                                            label="Offer Rate"
                                            value={
                                                <ServicingRate
                                                    servicingRate={rateRule?.offerRate}
                                                    referentPriceType={rateRule?.referentPriceType}
                                                />
                                            }
                                        />
                                    </div>
                                    {!formik.values.useContractRate && (
                                        <ActionRateOverride
                                            label="Offer Rate Override"
                                            helperText="This will override the offer rate, unless rate entered is less competitive than the Place of Service Override Rate."
                                            rateRule={rateRule}
                                        />
                                    )}
                                    <Checkbox
                                        id="forceTransferRoute"
                                        label="Ignore Routing"
                                        onChange={async (newValue) => {
                                            await formik.setFieldValue(
                                                'forceTransferRoute',
                                                newValue,
                                            );
                                        }}
                                        checked={formik.values.forceTransferRoute ?? false}
                                    />
                                    <Checkbox
                                        id="useContractRate"
                                        label="Use Contract Rate"
                                        onChange={(checked) =>
                                            formik.setValues({
                                                ...formik.values,
                                                useContractRate: checked,
                                                ...ActionRateOverride.initialValues,
                                            })
                                        }
                                        checked={formik.values.useContractRate}
                                    />
                                </>
                            )}
                        </>
                    )}
                    <ReasonInput reasons={UpdateSupplierReasons} />
                </>
            )}
        </>
    );
};

UpdateActiveReferralDataSupplier.propTypes = {
    treatmentDetails: PropTypes.shape({
        payerId: PropTypes.string.isRequired,
        productServiceOid: PropTypes.string.isRequired,
        placeOfService: PropTypes.string,
        isBrand: PropTypes.bool,
        benefitType: PropTypes.string,
        activeReferral: PropTypes.shape({
            servicingSupplierNpi: PropTypes.string,
        }),
    }),
    memberDetails: PropTypes.shape({
        memberId: PropTypes.string,
        firstName: PropTypes.string,
        memberState: PropTypes.string,
        dateOfBirth: PropTypes.string,
        lineOfBusiness: PropTypes.string,
        lobSegment: PropTypes.string,
    }),
};

UpdateActiveReferralDataSupplier.initialValues = () => ({ method: RouteCodes.ROFR });

UpdateActiveReferralDataSupplier.validationSchema = {
    supplierNpi: Yup.string().when('outOfNetworkPharmacy', {
        is: (outOfNetworkPharmacy) => outOfNetworkPharmacy !== true,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    outOfNetworkSupplierNpi: Yup.string().when('outOfNetworkPharmacy', {
        is: (outOfNetworkPharmacy) => outOfNetworkPharmacy === true,
        then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
    }),
    method: Yup.string()
        .oneOf(Object.values(RouteCodes))
        .required(validationStrings.requiredField.errorMessage),
    useContractRate: Yup.bool(),
    forceTransferRoute: Yup.bool(),
    ...ActionRateOverride.validationSchema,
    ...ReasonInput.validationSchema,
};

UpdateActiveReferralDataSupplier.onSubmit = async ({
    values,
    treatmentDetails,
    username,
    rateRule,
}) => {
    const { payerId, treatmentId } = treatmentDetails ?? {};

    const offerRate =
        values.method === RouteCodes.CONTRACT || values.useContractRate
            ? CONTRACT_RATE
            : ActionRateOverride.submitValue(values);

    await transferTreatment({
        payerId,
        treatmentId,
        newServicingPharmacyNpi: values.supplierNpi || values.outOfNetworkSupplierNpi,
        offerRate,
        forceTransferRoute:
            isFmhSupplier(values.supplierNpi) ||
            isFmhSupplier(values.outOfNetworkSupplierNpi) ||
            values?.forceTransferRoute,
        referentPriceType: rateRule?.referentPriceType,
        priceSource: rateRule?.priceSource,
        isFrozen: rateRule?.isFrozen,
        routeCode: values.method,
        ...ReasonInput.submitValues(values),
        username,
    });
};
